/**
 * Accessibility overlay
 *
 * Reads user preferences from localStorage, falls back to the browser's
 * prefers-color-scheme / prefers-reduced-motion media queries, applies body
 * classes, and keeps the overlay form in sync with the current state.
 *
 * Body classes:
 *   .dark-mode       — effective color scheme is dark
 *   .light-mode      — effective color scheme is light
 *   .reduce-motion   — motion should be reduced
 *
 * Form markup (see template-parts/overlays/accessibility.php):
 *   <input type="checkbox" data-accessibility-toggle="quietMode">
 *   <input type="radio"    data-accessibility-toggle="darkMode" value="auto|dark|light">
 */

const STORAGE = {
    darkMode:  'a11y:darkMode',   // 'auto' | 'dark' | 'light'
    quietMode: 'a11y:quietMode',  // '1' | '0'  (absent => follow system)
};

// Default when the visitor hasn't chosen yet. 'light' (bright) instead of
// 'auto' because the site reads better in light mode than in dark; visitors
// can still pick 'auto' or 'dark' in the accessibility overlay.
const DEFAULT_DARK_MODE = 'light';

const mqlDark          = window.matchMedia('(prefers-color-scheme: dark)');
const mqlReduceMotion  = window.matchMedia('(prefers-reduced-motion: reduce)');

const readDarkMode = () => {
    const v = localStorage.getItem(STORAGE.darkMode);
    return v === 'dark' || v === 'light' || v === 'auto' ? v : DEFAULT_DARK_MODE;
};

const readQuietMode = () => {
    const v = localStorage.getItem(STORAGE.quietMode);
    if (v === '1') return true;
    if (v === '0') return false;
    return mqlReduceMotion.matches;
};

const effectiveDarkMode = mode => {
    if (mode === 'dark')  return 'dark';
    if (mode === 'light') return 'light';
    return mqlDark.matches ? 'dark' : 'light';
};

const applyBodyClasses = () => {
    const effective = effectiveDarkMode(readDarkMode());
    document.body.classList.toggle('dark-mode',  effective === 'dark');
    document.body.classList.toggle('light-mode', effective === 'light');

    // Quiet mode can flip at runtime (the overlay checkbox, or the OS setting
    // while we're following it). Modules that already have motion running can't
    // see a class toggle, so announce it: `glunz:motionchange`, detail.reduced.
    const reduced = readQuietMode();
    const changed = document.body.classList.contains('reduce-motion') !== reduced;
    document.body.classList.toggle('reduce-motion', reduced);
    if (changed) {
        window.dispatchEvent(new CustomEvent('glunz:motionchange', { detail: { reduced } }));
    }
};

const syncForm = () => {
    const darkMode = readDarkMode();
    document.querySelectorAll('[data-accessibility-toggle="darkMode"]').forEach(el => {
        el.checked = el.value === darkMode;
    });
    const quiet = readQuietMode();
    document.querySelectorAll('[data-accessibility-toggle="quietMode"]').forEach(el => {
        el.checked = quiet;
    });
};

const bindForm = () => {
    document.querySelectorAll('[data-accessibility-toggle="darkMode"]').forEach(el => {
        el.addEventListener('change', () => {
            if (!el.checked) return;
            localStorage.setItem(STORAGE.darkMode, el.value);
            applyBodyClasses();
        });
    });

    document.querySelectorAll('[data-accessibility-toggle="quietMode"]').forEach(el => {
        el.addEventListener('change', () => {
            localStorage.setItem(STORAGE.quietMode, el.checked ? '1' : '0');
            applyBodyClasses();
            // The home hero (src/home.js) latches motion mode once, in init(), and
            // toggling at runtime leaves it in a mixed/broken state (WebGL canvas +
            // scroll-lock still live under the reduced layout). Only there do we
            // reload, so everything re-initialises cleanly in the newly-chosen mode;
            // the pre-paint script in header.php reads the just-written localStorage,
            // so there's no flash. Everywhere else the CSS class alone is enough.
            if (document.querySelector('[data-home-hero]')) location.reload();
        });
    });
};

const bindSystemListeners = () => {
    const onSystemDarkChange = () => {
        if (readDarkMode() === 'auto') applyBodyClasses();
    };
    const onSystemMotionChange = () => {
        if (localStorage.getItem(STORAGE.quietMode) === null) {
            applyBodyClasses();
            syncForm();
        }
    };

    if (mqlDark.addEventListener) {
        mqlDark.addEventListener('change', onSystemDarkChange);
        mqlReduceMotion.addEventListener('change', onSystemMotionChange);
    } else if (mqlDark.addListener) {
        // Safari < 14
        mqlDark.addListener(onSystemDarkChange);
        mqlReduceMotion.addListener(onSystemMotionChange);
    }
};

const init = () => {
    applyBodyClasses();
    syncForm();
    bindForm();
    bindSystemListeners();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
